import { trackEvent as trackEventBase } from '@zooplus/tracker-listener';

import { EVENTS_MAP } from '@config/consts';
import { useAppContext } from '@providers/AppContextProvider';
import { captureException } from '@services/sentryService';
import { TrackingData, TrackingFn, TrackingOptions } from '@types-internal/TrackingTypes';

const trackEvent = (event: TrackingData) =>
  (trackEventBase as (event: TrackingData) => void)(event);

/**
 * Custom hook to use tracking within the application
 *
 * @param options - Options needed to setup the tracking library
 * @param options.sid - Session ID
 * @param options.currency - Currency code
 * @param options.customerId - Customer ID
 * @param options.siteId - Site ID
 * @param options.guest - Guest user flag
 * @returns Function to track events
 *
 * @example
 * const track = useTracking({
 *  sid: '1234',
 *  currency: 'EUR',
 *  customerId: '1234',
 *  siteId: 1,
 *  guest: false,
 * });
 * track('AdyenFormOK');
 */
export const useTracking = (options?: TrackingOptions) => {
  const { locale } = useAppContext();
  const { sid, currency, customerId, siteId, guest } = options ?? {};

  if (!sid || !currency || !customerId) {
    return undefined;
  }

  const trackingFn: TrackingFn = (type, value) => {
    const knownEvent = EVENTS_MAP[type];

    if (typeof window === 'undefined') {
      captureException(new Error('useTracking called outside of browser'));
      return undefined;
    }

    if (!knownEvent) {
      captureException(new Error('Event does not exist'));
      return undefined;
    }

    let targetOrComponent = {};

    if (EVENTS_MAP[type].target) {
      targetOrComponent = {
        target: {
          ...EVENTS_MAP[type].target,
          value,
        },
      };
    } else {
      targetOrComponent = {
        component: {
          ...(EVENTS_MAP[type] as any).component,
          value,
        },
      };
    }

    const event = {
      event: knownEvent.event,
      page: knownEvent.page,
      detail: {
        sid,
        platform: 'WEB',
        domain: window.location.hostname,
        siteId,
        locale,
        currencyCode: currency,
        pageLocation: window.location.href,
        ...targetOrComponent,
        customer: {
          id: `${customerId}`,
          guest: guest,
        },
      },
    } as TrackingData;

    return trackEvent(event);
  };

  return trackingFn;
};
